import React, { useCallback, useEffect, useState } from 'react';
import EditorWrapper from '../../../editor/EditorWrapper';
import { TiptapMenuType } from '../../../editor/utils';
import {
  convertStringToTiptapOutput,
  getTextWithEntityPlaceholders,
  TiptapOutputType,
} from '@amzn/awscat-editor';
import constants from '../../../common/constants';
import { Box, FormField, Grid, SpaceBetween, StatusIndicator } from '@amzn/awsui-components-core';
import InfoLink from '../../common/InfoLink';
import InputSizeMessage from '../../utils/InputSizeMessage';
import CharacterCountMessage from '../../utils/CharacterCountMessage';
import styles from './MetadataEditorInputV2.module.scss';

/**
 * This component replaces MetadataEditorInput. In order to display the word counter
 * inline with the constraint text, we unfortunately could not use the inbuilt FormField
 * props. The old MetadataEditorInput component should be deprecated once all existing components
 * are moved over to use this component instead
 */
interface Props {
  onInputUpdated: (sectionId: string, value: string, validationError?: string) => void;
  onValidationError?: (validationError?: string) => void;
  sectionId: string;
  value: string;
  label: string;
  description?: string;
  contextualHelp?: string;
  isRequired?: boolean;
  stretch?: boolean;
  showValidationError?: boolean;
  showValidationWarnings?: boolean;
  disabled?: boolean;
  minLength: number;
  maxLength: number;
  removeFormatting?: boolean;
  singleLine?: boolean;
  hideMenuBar?: boolean;
  hideFooter?: boolean;
}

const MetadataEditorInputV2: React.FunctionComponent<Props> = (props: Props) => {
  const {
    onInputUpdated,
    value,
    label,
    description,
    contextualHelp,
    isRequired,
    showValidationError,
    showValidationWarnings,
    disabled,
    maxLength,
    minLength,
    onValidationError,
    sectionId,
    stretch,
    removeFormatting,
    singleLine,
    hideMenuBar,
    hideFooter,
  } = props;

  const [validationError, setValidationError] = useState<string>('');
  const [inputValue, setInputValue] = useState<string>(value);

  const validateInput = useCallback(
    (input: string): string => {
      let error: string = '';

      if (isRequired && !input) {
        error = constants.REQUIRED;
      } else if (input.length > 0) {
        // Validate length constraints for non-empty optional fields
        if (input.length < minLength || input.length > maxLength) {
          error = `Must be between ${minLength} and ${maxLength} characters`;
        }
      }

      if (onValidationError) {
        onValidationError(error);
      }
      setValidationError(error);
      return error;
    },
    [isRequired, minLength, maxLength, onValidationError],
  );

  useEffect(() => {
    validateInput(inputValue);
  }, [inputValue, validateInput]);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  useEffect(() => {
    if (showValidationError) {
      validateInput(inputValue);
    }
  }, [showValidationError, validateInput, inputValue]);

  const handleInput = (input: TiptapOutputType) => {
    const inputString = getTextWithEntityPlaceholders(input);
    const error = validateInput(inputString);

    setInputValue(inputString);

    if (sectionId && onInputUpdated) {
      onInputUpdated(sectionId, inputString, error);
    }
  };

  return (
    <SpaceBetween size="xxxs">
      <FormField
        label={
          <span>
            {label}
            {!isRequired && <i> - optional</i>}
          </span>
        }
        info={contextualHelp && <InfoLink topic={contextualHelp} />}
        description={description}
        stretch={stretch}
        i18nStrings={{ errorIconAriaLabel: 'Error' }}
      >
        <div className={styles.container}>
          <EditorWrapper
            model={convertStringToTiptapOutput(inputValue)}
            readOnly={false}
            onModelChange={handleInput}
            menuType={TiptapMenuType.Hide}
            disabled={disabled}
            removeFormatting={!!removeFormatting}
            singleLine={!!singleLine}
            hideMenuBar={hideMenuBar}
            hideFooter={hideFooter}
          />
        </div>
      </FormField>
      <Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
        <SpaceBetween size="s" direction="vertical">
          {showValidationError && validationError ? (
            <StatusIndicator type="warning" colorOverride="red">
              <Box variant="p" color="text-status-error" display="inline" fontSize="body-s">
                {validationError}
              </Box>
            </StatusIndicator>
          ) : (
            <InputSizeMessage input={inputValue} minNum={minLength} maxNum={maxLength} />
          )}
        </SpaceBetween>
        <CharacterCountMessage input={inputValue} />
      </Grid>
    </SpaceBetween>
  );
};

export default MetadataEditorInputV2;
